import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Typography,
  Alert,
  Space,
  Row,
  Col,
  Divider,
  Tag,
  message,
  Result,
  Progress,
  Spin,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  UserOutlined,
  CarOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getApprovedDealers,
  adminCreateSingleListing,
  type Dealer,
} from '../../api/services/bulkUploadService';
import { ImageUploader, type LocalImage } from '../../bulk-upload/components/ImageUploader';

const { Title, Text, Link } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const FUEL_TYPES = ['PETROL', 'DIESEL', 'ELECTRIC', 'HYBRID', 'LPG', 'OTHER'];
const TRANSMISSIONS = ['MANUAL', 'AUTOMATIC', 'SEMI_AUTOMATIC'];
const BODY_TYPES = ['SEDAN', 'SUV', 'HATCHBACK', 'COUPE', 'WAGON', 'VAN', 'TRUCK', 'CONVERTIBLE', 'OTHER'];
const CONDITIONS = ['NEW', 'USED', 'CERTIFIED_PRE_OWNED'];
const FEATURES_OPTIONS = [
  'Air Conditioning', 'Bluetooth', 'Navigation', 'Leather Seats',
  'Sunroof', 'Parking Sensors', 'Backup Camera', 'Heated Seats',
  'Apple CarPlay', 'Android Auto', 'Cruise Control', 'Alloy Wheels',
  'Keyless Entry', 'Push Start', 'Adaptive Cruise Control',
];

interface ListingResult {
  listing: { id: string; make: string; model: string; year: number; slug?: string };
  imageCount: number;
  imageErrors: Array<{ filename: string; error: string }>;
}

const AdminSingleListing: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [loadingDealers, setLoadingDealers] = useState(true);
  const [localImages, setLocalImages] = useState<LocalImage[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState<ListingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await getApprovedDealers();
        setDealers(d);
      } catch {
        // ignore — form will show empty dealer list
      } finally {
        setLoadingDealers(false);
      }
    })();
  }, []);

  const handleSubmit = async (values: any) => {
    const validImages = localImages.filter(i => i.valid);
    setSaving(true);
    setUploadProgress(0);
    setError(null);
    try {
      const data = {
        dealerId: values.dealerId,
        make: values.make,
        model: values.model,
        year: values.year,
        price: values.price,
        mileage: values.mileage,
        color: values.color,
        fuelType: values.fuelType,
        transmission: values.transmission,
        bodyType: values.bodyType,
        condition: values.condition,
        description: values.description,
        title: values.title,
        isNegotiable: values.isNegotiable ?? true,
        location: values.location,
        city: values.city,
        country: values.country || 'Albania',
        doors: values.doors,
        seats: values.seats,
        features: (values.features as string[] | undefined)?.join('|'),
        displacement: values.displacement,
        driveType: values.driveType,
        numberOfOwners: values.numberOfOwners,
      };

      const res = await adminCreateSingleListing(
        data,
        validImages.map(i => i.file),
        setUploadProgress,
      );

      setResult({
        listing: res.listing,
        imageCount: res.imageCount,
        imageErrors: res.imageErrors,
      });
      message.success(t('admin.single_listing.msg_created'));
    } catch (err: any) {
      const msg = err?.message || t('admin.single_listing.msg_create_failed');
      setError(msg);
      message.error(msg);
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setLocalImages([]);
    setResult(null);
    setError(null);
  };

  if (result) {
    return (
      <Card>
        <Result
          status="success"
          title={t('admin.single_listing.result_title')}
          subTitle={t('admin.single_listing.result_subtitle', { make: result.listing.make, model: result.listing.model, year: result.listing.year, count: result.imageCount })}
          extra={[
            <Button
              key="new"
              type="primary"
              onClick={handleReset}
              icon={<ReloadOutlined />}
              style={{ background: '#fe5a1d', borderColor: '#fe5a1d' }}
            >
              {t('admin.single_listing.btn_create_another')}
            </Button>,
          ]}
        />
        {result.imageErrors.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
            message={t('admin.single_listing.alert_image_errors', { count: result.imageErrors.length })}
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {result.imageErrors.map((e, i) => (
                  <li key={i} style={{ fontSize: 12 }}>
                    {e.filename}: {e.error}
                  </li>
                ))}
              </ul>
            }
          />
        )}
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <Tag color="green">{t('admin.single_listing.tag_id_label')} {result.listing.id.slice(0, 8)}…</Tag>
          {result.listing.slug && (
            <Tag color="blue">{t('admin.single_listing.tag_slug_label')} {result.listing.slug}</Tag>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0 }}>{t('admin.single_listing.page_title')}</Title>
        <Text type="secondary">
          {t('admin.single_listing.page_subtitle')}
        </Text>
      </div>

      {error && (
        <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} closable onClose={() => setError(null)} />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ isNegotiable: true, country: 'Albania' }}
      >
        {/* ── Dealer ── */}
        <div style={{ background: '#fafafa', padding: '16px 20px', borderRadius: 8, marginBottom: 20, border: '1px solid #f0f0f0' }}>
          <Title level={5} style={{ margin: '0 0 12px', color: '#fe5a1d' }}>
            <UserOutlined /> {t('admin.single_listing.section_dealer')}
          </Title>
          {loadingDealers ? (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Spin size="small" /> <Text type="secondary"> {t('admin.single_listing.loading_dealers')}</Text>
            </div>
          ) : (
            <Form.Item
              name="dealerId"
              label={t('admin.single_listing.label_select_dealer')}
              rules={[{ required: true, message: t('admin.single_listing.validation_select_dealer') }]}
            >
              <Select
                showSearch
                placeholder={t('admin.single_listing.ph_choose_dealer')}
                optionFilterProp="label"
                size="large"
              >
                {dealers.map(d => (
                  <Option key={d.id} value={d.id} label={`${d.companyName} ${d.email}`}>
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                      <span style={{ fontWeight: 600 }}>{d.companyName}{d.city ? ` — ${d.city}` : ''}</span>
                      <span style={{ fontSize: 12, color: '#888' }}>{d.email}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </div>

        {/* ── Vehicle ── */}
        <div style={{ background: '#fafafa', padding: '16px 20px', borderRadius: 8, marginBottom: 20, border: '1px solid #f0f0f0' }}>
          <Title level={5} style={{ margin: '0 0 12px', color: '#fe5a1d' }}>
            <CarOutlined /> {t('admin.edit_listing.section_vehicle_details')}
          </Title>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="make" label={t('admin.edit_listing.label_make')} rules={[{ required: true }]}>
                <Input placeholder={t('admin.single_listing.ph_make')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="model" label={t('admin.edit_listing.label_model')} rules={[{ required: true }]}>
                <Input placeholder={t('admin.single_listing.ph_model')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="year" label={t('admin.edit_listing.label_year')} rules={[{ required: true }]}>
                <InputNumber min={1900} max={new Date().getFullYear() + 1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="price" label={t('admin.edit_listing.label_price')} rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} formatter={v => `€ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mileage" label={t('admin.edit_listing.label_mileage')}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="color" label={t('admin.edit_listing.label_color')}>
                <Input placeholder={t('admin.single_listing.ph_color')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fuelType" label={t('admin.edit_listing.label_fuel_type')}>
                <Select placeholder={t('admin.edit_listing.ph_select')}>
                  {FUEL_TYPES.map(f => <Option key={f} value={f}>{f}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="transmission" label={t('admin.edit_listing.label_transmission')}>
                <Select placeholder={t('admin.edit_listing.ph_select')}>
                  {TRANSMISSIONS.map(tr => <Option key={tr} value={tr}>{tr}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bodyType" label={t('admin.edit_listing.label_body_type')}>
                <Select placeholder={t('admin.edit_listing.ph_select')}>
                  {BODY_TYPES.map(b => <Option key={b} value={b}>{b}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="condition" label={t('admin.edit_listing.label_condition')}>
                <Select placeholder={t('admin.edit_listing.ph_select')}>
                  {CONDITIONS.map(c => <Option key={c} value={c}>{c}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="doors" label={t('admin.edit_listing.label_doors')}>
                <InputNumber min={1} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="seats" label={t('admin.edit_listing.label_seats')}>
                <InputNumber min={1} max={20} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="displacement" label={t('admin.edit_listing.label_displacement')}>
                <Input placeholder={t('admin.edit_listing.ph_displacement')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="driveType" label={t('admin.edit_listing.label_drive_type')}>
                <Select placeholder={t('admin.edit_listing.ph_select')} allowClear>
                  <Option value="front_wheel_drive">FWD (Front Wheel Drive)</Option>
                  <Option value="rear_wheel_drive">RWD (Rear Wheel Drive)</Option>
                  <Option value="all_wheel_drive">AWD / 4WD (All Wheel Drive)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="numberOfOwners" label={t('admin.edit_listing.label_num_owners')}>
                <InputNumber min={0} max={20} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="title" label={t('admin.single_listing.label_title_optional')}>
            <Input placeholder={t('admin.edit_listing.ph_title')} />
          </Form.Item>

          <Form.Item name="description" label={t('admin.edit_listing.label_description')}>
            <TextArea rows={4} placeholder={t('admin.edit_listing.ph_description')} />
          </Form.Item>

          <Form.Item name="features" label={t('admin.edit_listing.label_features')}>
            <Select mode="multiple" placeholder={t('admin.edit_listing.ph_select_features')} allowClear>
              {FEATURES_OPTIONS.map(f => <Option key={f} value={f}>{f}</Option>)}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="location" label={t('admin.edit_listing.label_address')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="city" label={t('admin.edit_listing.label_city')}>
                <Input placeholder={t('admin.edit_listing.ph_city')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="country" label={t('admin.edit_listing.label_country')}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="isNegotiable" label={t('admin.edit_listing.label_negotiable')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>

        {/* ── Images ── */}
        <div style={{ background: '#fafafa', padding: '16px 20px', borderRadius: 8, marginBottom: 20, border: '1px solid #f0f0f0' }}>
          <Title level={5} style={{ margin: '0 0 12px', color: '#fe5a1d' }}>
            <PictureOutlined /> {t('admin.single_listing.section_images_title')}
          </Title>
          <ImageUploader
            images={localImages}
            onChange={setLocalImages}
            uploading={saving}
            uploadProgress={uploadProgress}
            disabled={saving}
          />
        </div>

        {/* Upload progress */}
        {saving && uploadProgress > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Progress percent={uploadProgress} strokeColor="#fe5a1d" />
            <Text type="secondary" style={{ fontSize: 12 }}>{t('admin.single_listing.uploading_processing')}</Text>
          </div>
        )}

        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            icon={<SaveOutlined />}
            size="large"
            style={{ background: '#fe5a1d', borderColor: '#fe5a1d' }}
          >
            {t('admin.single_listing.btn_create_listing')}
          </Button>
          <Button onClick={handleReset} disabled={saving}>
            {t('admin.single_listing.btn_reset')}
          </Button>
        </Space>
      </Form>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 20 }}
        message={t('admin.single_listing.alert_admin_listing')}
        description={t('admin.single_listing.alert_admin_listing_desc')}
      />
    </Card>
  );
};

export default AdminSingleListing;
